import React, { Fragment, useEffect, useState } from 'react';
import { WMSFetch } from '../utils/common-api-calls';
import { useAuth } from '../context/wms-context';
import { formatInputText } from '../utils/common-utils';
import { Dialog, Transition } from '@headlessui/react';
import Document8130Form from '../components/guidedInterface/forms/formOverrides/Document8130Form';
import { defaultDarValue } from '../components/guidedInterface/forms/utils/data';
import { printGeneratedDocuments } from '../components/guidedInterface/forms/utils/functions';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import moment from 'moment';
import Notification from '../../components/Notification';

export default function WMS8130() {
    const { currentUser } = useAuth();
    const [releaseNumber, setReleaseNumber] = useState(null);
    const [formOverrides, setFormOverrides] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [releaseLineData, setReleaseLineData] = useState(null);
    const [releaseData, setReleaseData] = useState(null);
    const [printingForm, setPrintingForm] = useState(false);
    const [showNotification, setShowNotification] = useState({ shown: false, status: null, title: '', message: '' });

    const resetData = () => {
        setReleaseLineData(null);
        setFormOverrides(null);
    };

    const closeNotification = () => {
        setShowNotification({ shown: false, status: null, title: '', message: '' });
    };

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            setLoading(true);
            const updatedReleaseNumber = formatInputText(releaseNumber, 'RLS-');
            const body = { releaseNumber: updatedReleaseNumber };
            const data = await WMSFetch('wms/documents/get-8130-info', body, currentUser);
            if (!data) throw Error();

            setReleaseLineData(data.documentInfo);
            setReleaseData(data.releaseData);
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error Getting data for release',
                message: 'An error has occured getting information for the entered Release Number. If the error persists please contact your manager.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPrintForm = (releaseLineName) => {
        const matchingDocumentData = releaseLineData.find((data) => data.releaseLineName === releaseLineName);
        setFormOverrides(matchingDocumentData?.document);

        setModalOpen(true);
    };

    const dynamicallyParseFormData = (event, formName) => {
        const inputType = event.target.type;
        const keyName = event.target.name;
        const value = inputType === 'checkbox' ? event.target.checked : event.target.value;

        const overrides = { ...formOverrides };

        if (keyName === 'darDisplayName') {
            const darOptions = overrides.form8130.DAR;
            const selectedOption = darOptions?.find((dar) => dar.displayName === value) ?? defaultDarValue;
            const { authorizationName, authorizationNumber, displayName } = selectedOption;

            overrides.form8130.DAR = overrides.form8130.DAR.map((dar) => {
                if (dar.displayName === displayName) {
                    return { ...dar, priority: true };
                }
                return { ...dar, priority: false };
            });
            overrides.form8130.authorizationName = authorizationName;
            overrides.form8130.authorizationNumber = authorizationNumber;
            overrides.form8130.darDisplayName = displayName;
            return;
        }

        if (keyName === 'showLeft' || keyName === 'showRight') {
            if (keyName === 'showLeft' && overrides.form8130.showRight === true) {
                overrides.form8130.showLeft = true;
                overrides.form8130.showRight = false;
            } else if (keyName === 'showRight' && overrides.form8130.showLeft === true) {
                overrides.form8130.showLeft = false;
                overrides.form8130.showRight = true;
            }
            setFormOverrides(() => {
                return overrides;
            });
            return;
        }

        overrides[formName][keyName] = value;
        setFormOverrides(overrides);
    };

    const printForm = async (releaseLineName) => {
        try {
            setPrintingForm(true);
            const documentInputs = formOverrides.form8130;
            const documentDataToSend = { form8130: [documentInputs] };
            await printGeneratedDocuments(documentDataToSend, releaseLineName, releaseData.releaseName, currentUser, false, false);
            updatePrintedDate(releaseLineName);
            setModalOpen(false);
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error Getting Printing Form',
                message: 'An error has occured during the printing process please try again. If the error persists please contact your manager.',
            });
        } finally {
            setPrintingForm(false);
        }
    };

    const updatePrintedDate = (releaseLineName) => {
        const date = moment().format('MM/DD/YYYY');
        const updatedReleaseLineData = releaseLineData.map((line) => {
            if (line.releaseLineName === releaseLineName) {
                return {
                    ...line,
                    lastPrintedDate: date,
                }
            }
            return line
        })
        setReleaseLineData(updatedReleaseLineData);
    }

    useEffect(() => {
        if (showNotification.shown) {
            setTimeout(() => {
                closeNotification();
            }, 5000);
        }
    }, [showNotification]);

    if (loading) {
        return (
            <div className='flex flex-col h-[calc(100vh-64px)] items-center justify-center'>
                <div className='h-28 w-28 border-b-[5px] border-e-[5px] mb-7 border-gray-500 rounded-full animate-spin out' />
                <p className='text-xl'>Retreiving Data for Release</p>
            </div>
        );
    }

    return (
        <>
            <Notification
                showSavedNotification={showNotification.shown}
                setShowSavedNotification={closeNotification}
                status={showNotification.status}
                title={showNotification.title}
                desc={showNotification.message}
            />
            <Form8130Popup
                isOpen={modalOpen}
                setIsOpen={setModalOpen}
                formData={formOverrides}
                dynamicallyParseFormData={dynamicallyParseFormData}
                printForm={printForm}
                printingForm={printingForm}
                setPrintingForm={setPrintingForm}
            />
            {releaseLineData ? (
                <section className='w-full px-8'>
                    <header className='flex h-10 mt-10'>
                        <button
                            className='flex items-center justify-center w-24 h-10 px-3 my-auto text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary'
                            onClick={resetData}
                        >
                            <ArrowLeftIcon className='w-6 h-6 mr-2' />
                            <span>Back</span>
                        </button>
                    </header>
                    <div className='pt-8 flex flex-col items-center justify-center flex-1 text-center'>
                        <h1 className='text-3xl font-bold'>
                            Release Lines for{' '}
                            <a
                                className='text-blue-800 underline hover:text-blue-400'
                                href={releaseData.releaseLink}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {releaseData.releaseName}
                            </a>
                        </h1>
                    </div>
                    {releaseLineData?.some((line) => line.previouslyPrinted && line.lastPrintedDate === 'N/A') ? (
                        <h1 className='w-full text-end text-red-600 mt-3'>
                            * This document was printed before adding printed dates to each of the documents when they are
                            printed.
                        </h1>
                    ) : null}
                    <table className='w-full mt-3'>
                        <thead className='px-2 text-white bg-blue-primary'>
                            <tr className='border-b'>
                                <th className='pl-3'>Release Line #</th>
                                <th>Release Line Name</th>
                                <th>Last Printed Date</th>
                                <th>Print Form</th>
                            </tr>
                        </thead>
                        <tbody>
                            {releaseLineData
                                .filter((line) => line.hasInventory)
                                .map((line) => {
                                    return <ReleaseLineRow line={line} handleOpenPrintForm={handleOpenPrintForm} />;
                                })}
                            {releaseLineData
                                .filter((line) => !line.hasInventory)
                                .map((line) => {
                                    return <ReleaseLineRow line={line} handleOpenPrintForm={handleOpenPrintForm} />;
                                })}
                        </tbody>
                    </table>
                </section>
            ) : (
                <section className='flex h-[calc(100vh-64px)] items-center justify-center'>
                    <form onSubmit={handleSubmit} className='flex flex-col items-center'>
                        <label htmlFor='releaseNumber' className='mb-2 text-lg'>
                            Release Number
                        </label>
                        <input
                            id='releaseNumber'
                            type='text'
                            value={releaseNumber}
                            onChange={(e) => setReleaseNumber(e.target.value)}
                            className='p-2 border border-gray-300 rounded'
                            autoFocus
                        />
                        <button type='submit' className='mt-4 px-4 py-2 bg-blue-primary text-white rounded'>
                            Submit
                        </button>
                    </form>
                </section>
            )}
        </>
    );
}

function ReleaseLineRow({ line, handleOpenPrintForm }) {
    return (
        <tr key={line.releaseLineName} className='h-10 border-b border-gray-400 odd:bg-gray-200 even:bg-gray-50'>
            <td className='pl-3'>{line.lineNumber}</td>
            <td>
                <a
                    className='text-blue-800 underline cursor-pointer hover:text-blue-400'
                    href={line.releaseLineLink}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {line.releaseLineName}
                </a>
            </td>
            <td className='relative'>
                {line.lastPrintedDate}
                {line.previouslyPrinted && line.lastPrintedDate === 'N/A' ? (
                    <h1 className='absolute -top-1 -left-3 text-red-600 text-lg font-bold'>*</h1>
                ) : null}
            </td>
            <td>
                {line.hasInventory ? (
                    <button
                        className='rounded-md bg-blue-primary text-white px-3 py-1 my-1'
                        onClick={() => handleOpenPrintForm(line.releaseLineName)}
                    >
                        {line.previouslyPrinted ? 'Re-Print' : 'Print'}
                    </button>
                ) : null}
            </td>
        </tr>
    );
}

function Form8130Popup({ printForm, isOpen, setIsOpen, formData, dynamicallyParseFormData, printingForm }) {
    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog className={'relative z-10'} onClose={() => {}}>
                <section className='fixed w-screen h-screen top-0 left-0 bg-gray-500 opacity-75 z-10' />
                <section className='fixed top-0 left-0 z-20 flex items-center justify-center w-screen h-screen'>
                    <section className='fixed flex flex-col justify-between bg-white rounded-lg px-4 py-7 w-[800px] h-[750px] z-20'>
                        <header className='w-full flex items-center justify-center pb-2'>
                            <h1 className='text-xl'>Form 8130</h1>
                        </header>
                        <form 
                            className='w-full h-full overflow-y-auto my-3 px-3'
                            onChange={(e) => {
                                dynamicallyParseFormData(e, 'form8130');
                            }}
                        >
                            <Document8130Form
                                documentDefaults={formData}
                                formName={''}
                                showForm={true}
                            />
                        </form>
                        <footer className='w-full flex flex-row justify-center gap-5'>
                            <button
                                type='button'
                                className='mt-3 inline-flex w-fit justify-center rounded-md bg-white px-7 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:!bg-gray-50 sm:col-start-1 sm:mt-0'
                                onClick={() => setIsOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                disabled={printingForm}
                                className={
                                    'inline-flex w-fit justify-center rounded-md px-7 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:col-start-2 !bg-blue-primary'
                                }
                                onClick={() => {
                                    printForm(formData.form8130.releaseLineNumber);
                                }}
                            >
                                {printingForm ? 'Printing Form...' : 'Print'}
                            </button>
                        </footer>
                    </section>
                </section>
            </Dialog>
        </Transition.Root>
    );
}
